// ── BCHNostr — BCH Video ──────────────────────────────────────────
//
// BCHNostr is a DISCOVERY, VIEWING, DISCUSSION and MONETIZATION layer for
// video. It is emphatically NOT a video host. The division of labour is
// the entire design:
//
//   IDENTITY        Nostr (the creator's existing key — no second account)
//   VIDEO HOSTING   YouTube / PeerTube / Blossom / any external provider
//   METADATA        one small addressable Nostr event per video
//   DISCOVERY       BCHNostr (/videos)
//   DISCUSSION      Nostr (NIP-22 comments on the video's coordinate)
//   PAYMENTS        Bitcoin Cash (the existing tip / boost / membership rails)
//   PROMOTION       BCHNostr (the existing boost auction)
//
// Three hard rules, and everything downstream depends on them:
//
//   1. Video bytes are NEVER hosted, copied, transcoded, cached or proxied
//      by BCHNostr. We store a URL and play it from its origin. Only tiny
//      oEmbed JSON documents pass through the shared read-only proxy.
//   2. Metadata rides on an EXISTING Nostr structure — NIP-71 addressable
//      video events (kind 34235) — so a future Nostr-native video protocol
//      can be adopted without rebuilding the feature.
//   3. Everything in a video event is UNTRUSTED, even though it is signed.
//      A signature proves authorship, not that a title is free of markup or
//      that a thumbnail URL is safe to put in an `img src`. This module is
//      the single sanitizing boundary; no component re-parses raw content.

import type { Event } from 'nostr-tools';
import { PLATFORM_FEE_ADDRESS } from './platform';
import { safeUrl, safeUrlLoose, stripHtml } from './podcast';
import {
  resolveVideoUrl, parseVideoUrl, providerLabel,
  type ProviderId, type ResolvedVideo,
} from './videoProviders';

// ── Kinds & tags ──────────────────────────────────────────────────

/**
 * NIP-71 addressable normal video event.
 *
 * Addressable (rather than the regular kind 21) so a creator can fix a
 * typo, re-point a moved URL, or refresh a thumbnail without orphaning the
 * discussion thread, the tips, or the playlists that reference it.
 */
export const VIDEO_KIND = 34235;
/** Relay-indexable discovery tag every BCHNostr video carries. */
export const VIDEO_TAG = 'bch-video';
/** Prefix for the indexable category tag. */
export const VIDEO_CAT_PREFIX = 'bch-vid-cat-';

/** Video discussion — NIP-22 comment rooted on the video's coordinate. */
export const VIDEO_COMMENT_KIND = 1111;
/** Discovery tag on comments, so a creator can find discussion of their work. */
export const VIDEO_COMMENT_TAG = 'bch-video-comment';

/** NIP-51 curation set of videos — a playlist. */
export const PLAYLIST_KIND = 30005;
export const PLAYLIST_TAG = 'bch-video-playlist';

/** Video promotion, riding the existing boost auction. */
export const VIDEO_BOOST_KIND = 30078;
export const VIDEO_BOOST_TAG = 'bch-video-boost';
export const VIDEO_BOOST_D_TAG_PREFIX = 'bchnostr/video-boost/';
export const VIDEO_BOOST_WALLET = PLATFORM_FEE_ADDRESS;
/** The same 6h window every other boost in this app uses. */
export const VIDEO_BOOST_DURATION = 6 * 3600;
export const VIDEO_BOOST_MIN_BID_USD = 0.25;
export const VIDEO_BOOST_BID_PRESETS = [
  { usd: 0.25, label: '$0.25', emoji: '\u26A1' },
  { usd: 1,    label: '$1',    emoji: '\u{1F525}' },
  { usd: 3,    label: '$3',    emoji: '\u{1F680}' },
  { usd: 5,    label: '$5',    emoji: '\u{1F451}' },
];
/** How many promoted videos are surfaced at once. */
export const VIDEO_BOOST_MAX_VISIBLE = 3;

/** Tip receipt kind, shared with the rest of the app's BCH rails. */
export const TIP_RECEIPT_KIND = 9735;

// ── Categories ────────────────────────────────────────────────────

export const VIDEO_CATEGORIES: Array<{ id: string; label: string }> = [
  { id: 'bch',         label: 'Bitcoin Cash' },
  { id: 'tutorial',    label: 'Tutorials & How-To' },
  { id: 'development', label: 'Development' },
  { id: 'merchant',    label: 'Merchant Adoption' },
  { id: 'news',        label: 'News & Analysis' },
  { id: 'interview',   label: 'Interviews' },
  { id: 'conference',  label: 'Conferences & Talks' },
  { id: 'product',     label: 'Product & Demos' },
  { id: 'education',   label: 'Education' },
  { id: 'entertainment', label: 'Entertainment' },
  { id: 'other',       label: 'Other' },
];

const VIDEO_CATEGORY_IDS = new Set(VIDEO_CATEGORIES.map(c => c.id));

export function videoCategoryLabel(id: string | undefined): string {
  return VIDEO_CATEGORIES.find(c => c.id === id)?.label || 'Other';
}

/** Narrow an untrusted category string to a known id. */
export function normalizeCategory(raw: string | undefined | null): string {
  const v = (raw || '').trim().toLowerCase();
  return VIDEO_CATEGORY_IDS.has(v) ? v : 'other';
}

// ── Shape ─────────────────────────────────────────────────────────

/** A video as published by its creator. All fields are already sanitized. */
export interface BCHVideo {
  /** The event's `d` tag — stable id, derived from the source URL. */
  id: string;
  /** The creator's pubkey. Their Nostr identity IS their channel. */
  pubkey: string;
  title: string;
  /** Plain text. Never markup. */
  description: string;
  /** The external playback URL. BCHNostr never serves these bytes. */
  url: string;
  /** Poster image (https only). May be ''. */
  thumbnailUrl: string;
  /** Seconds. 0 when unknown. */
  duration: number;
  category: string;
  /** Free-form hashtags, lowercased and capped. */
  hashtags: string[];
  /** Unix seconds the video was first published (may predate the event). */
  publishedAt: number;
  created_at: number;
  /** Which provider serves the media. */
  provider: ProviderId;
  /**
   * View count reported BY THE PROVIDER. 0 when the provider does not
   * publish one — which is almost always. We never invent this number and
   * never present a BCHNostr counter as an external view count.
   */
  externalViews: number;
  /** Resolved playback details, or null when the URL is unusable. */
  playback: ResolvedVideo | null;
  event: Event;
}

/** A playlist: an ordered list of references to videos. Never copies media. */
export interface VideoPlaylist {
  /** The event's `d` tag. */
  id: string;
  pubkey: string;
  title: string;
  description: string;
  image: string;
  /** Video coordinates in order, `34235:<pubkey>:<d>`. */
  videoRefs: string[];
  created_at: number;
  event: Event;
}

// ── Identity & routing ────────────────────────────────────────────
//
// A video's identity is derived from its SOURCE URL, hashed to a short
// url-safe token. Two consequences, both deliberate:
//
//   · Re-adding the same URL REPLACES the existing entry instead of
//     creating a duplicate (34235 is addressable, keyed on `d`).
//   · The `d` tag is safe to place in a route path segment, which a raw
//     URL is not.

/** Short, url-safe, stable token for an arbitrary string. */
function shortHash(input: string): string {
  let h1 = 0x811c9dc5;
  let h2 = 0x01000193;
  for (let i = 0; i < input.length; i++) {
    const c = input.charCodeAt(i);
    h1 = Math.imul(h1 ^ c, 0x01000193) >>> 0;
    h2 = Math.imul(h2 + c, 0x85ebca6b) >>> 0;
  }
  return h1.toString(36) + h2.toString(36);
}

/**
 * The `d` tag for a video, derived from its canonical source URL.
 *
 * Canonical, not raw: two URLs for the same YouTube video (youtu.be/X,
 * watch?v=X&t=30) resolve to one id, so they can't both be added.
 */
export function videoDTag(rawUrl: string): string {
  const resolved = resolveVideoUrl(rawUrl);
  return shortHash(resolved ? `${resolved.provider}:${resolved.id}` : rawUrl.trim());
}

/** Coordinate for an addressable video, used by comments and playlists. */
export function videoCoordinate(pubkey: string, dTag: string): string {
  return `${VIDEO_KIND}:${pubkey}:${dTag}`;
}

/** Parse a `34235:<pubkey>:<d>` coordinate. Null when malformed. */
export function parseVideoCoordinate(
  raw: string | undefined | null,
): { pubkey: string; dTag: string } | null {
  if (!raw || typeof raw !== 'string') return null;
  const parts = raw.split(':');
  if (parts.length < 3) return null;
  if (parts[0] !== String(VIDEO_KIND)) return null;
  if (!/^[0-9a-f]{64}$/i.test(parts[1])) return null;
  // Rejoin — a `d` tag may itself contain a colon.
  const dTag = parts.slice(2).join(':');
  if (!dTag) return null;
  return { pubkey: parts[1].toLowerCase(), dTag };
}

/** In-app path for a video watch page. */
export function videoWatchPath(pubkey: string, dTag: string): string {
  return `/videos/watch/${pubkey}/${encodeURIComponent(dTag)}`;
}

/** In-app path for a creator's video channel. */
export function videoChannelPath(pubkey: string): string {
  return `/videos/channel/${pubkey}`;
}

/** In-app path for a playlist. */
export function playlistPath(pubkey: string, dTag: string): string {
  return `/videos/playlist/${pubkey}/${encodeURIComponent(dTag)}`;
}

/** A url-safe `d` tag for a new playlist. */
export function playlistDTag(title: string): string {
  const slug = title.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '').slice(0, 40);
  return `${slug || 'playlist'}-${shortHash(`${title}${Date.now()}`)}`;
}

// ── Text limits ───────────────────────────────────────────────────

const MAX_TITLE = 300;
const MAX_DESCRIPTION = 5000;
const MAX_HASHTAGS = 12;
/** A video longer than 24h is a mangled duration, not a video. */
const MAX_DURATION = 24 * 3600;

function clip(raw: string | undefined | null, max: number): string {
  if (!raw || typeof raw !== 'string') return '';
  const value = raw.trim();
  return value.length > max ? value.slice(0, max).trimEnd() : value;
}

/** Normalize a hashtag: lowercase, alphanumeric-ish, no leading '#'. */
function normalizeHashtag(raw: string): string {
  return raw.trim().toLowerCase().replace(/^#+/, '').replace(/[^a-z0-9_-]/g, '').slice(0, 40);
}

/** Parse a comma/space separated hashtag string into clean tags. */
export function parseHashtagInput(raw: string): string[] {
  return [...new Set(
    raw.split(/[,\s]+/).map(normalizeHashtag).filter(Boolean),
  )].slice(0, MAX_HASHTAGS);
}

// ── Build the video event ─────────────────────────────────────────

export interface VideoDraft {
  /** The external URL. Required — without it there is nothing to watch. */
  url: string;
  title: string;
  description: string;
  thumbnailUrl: string;
  /** Seconds. 0 when unknown. */
  duration: number;
  category: string;
  hashtags: string[];
  /** Unix seconds; defaults to now. Preserved across edits. */
  publishedAt?: number;
}

/**
 * Build tags + content for a VIDEO_KIND publish.
 *
 * The media pointer goes in a NIP-92 `imeta` tag (NIP-71's canonical home
 * for it) so any NIP-71 client can play the video without knowing anything
 * about BCHNostr. The `origin` tag records which platform actually hosts
 * it, which is the honest statement of where the bytes live.
 *
 * Returns null when the URL isn't usable at all — we refuse to publish a
 * video event with nothing to play.
 */
export function buildVideoEvent(
  draft: VideoDraft,
): { dTag: string; tags: string[][]; content: string } | null {
  const resolved = resolveVideoUrl(draft.url);
  if (!resolved) return null;

  const dTag = videoDTag(draft.url);
  const category = normalizeCategory(draft.category);
  const title = clip(stripHtml(draft.title, MAX_TITLE), MAX_TITLE) || 'Untitled video';
  const description = clip(stripHtml(draft.description, MAX_DESCRIPTION), MAX_DESCRIPTION);
  const thumbnailUrl = safeUrlLoose(draft.thumbnailUrl) || resolved.thumbnailUrl;
  const duration = Number.isFinite(draft.duration) && draft.duration > 0
    ? Math.min(Math.floor(draft.duration), MAX_DURATION)
    : 0;
  const publishedAt = draft.publishedAt && draft.publishedAt > 0
    ? Math.floor(draft.publishedAt)
    : Math.floor(Date.now() / 1000);

  // NIP-92 imeta. `url` is the canonical page/file on the provider — the
  // one and only place playback comes from.
  const imeta = ['imeta', `url ${resolved.sourceUrl}`];
  if (thumbnailUrl) imeta.push(`image ${thumbnailUrl}`);
  if (duration > 0) imeta.push(`duration ${duration}`);
  if (resolved.playback === 'file') imeta.push('m video/mp4');
  if (resolved.provider === 'nostrmedia') imeta.push('service nip96');

  const tags: string[][] = [
    ['d', dTag],
    ['title', title],
    ['published_at', String(publishedAt)],
    imeta,
    // NIP-71 `origin`: platform, external id, original URL. This is the
    // machine-readable statement that BCHNostr is not the host.
    ['origin', resolved.provider, resolved.id, resolved.sourceUrl],
    // A plain `r` reference too, for clients that only understand links.
    ['r', resolved.sourceUrl],
    ['t', VIDEO_TAG],
    ['t', `${VIDEO_CAT_PREFIX}${category}`],
    ['alt', `Video: ${title}`],
  ];
  if (duration > 0) tags.push(['duration', String(duration)]);
  for (const tag of draft.hashtags.map(normalizeHashtag).filter(Boolean).slice(0, MAX_HASHTAGS)) {
    // Skip collisions with our own reserved discovery tags.
    if (tag === VIDEO_TAG || tag.startsWith(VIDEO_CAT_PREFIX)) continue;
    tags.push(['t', tag]);
  }

  return { dTag, tags, content: description };
}

/** Tags + content that retire a video (published when a creator removes it). */
export function retireVideoEvent(dTag: string): { tags: string[][]; content: string } {
  return {
    tags: [
      ['d', dTag],
      ['alt', 'Video removed'],
    ],
    content: '',
  };
}

// ── Parse the video event ─────────────────────────────────────────

/** Read one space-separated key from a NIP-92 imeta tag. */
function imetaValue(imeta: string[], key: string): string {
  for (let i = 1; i < imeta.length; i++) {
    const entry = imeta[i];
    if (typeof entry !== 'string') continue;
    if (entry.startsWith(`${key} `)) return entry.slice(key.length + 1).trim();
  }
  return '';
}

/**
 * Is this event a live (non-retired) BCHNostr video?
 *
 * Requires the discovery tag, so a random NIP-71 event from elsewhere on
 * the network isn't silently absorbed into BCHNostr's feeds. Content may
 * legitimately be empty (a video with no description), so emptiness alone
 * is not the retirement signal — the missing `t` tag is.
 */
export function isActiveVideo(ev: Event | null | undefined): boolean {
  if (!ev || ev.kind !== VIDEO_KIND) return false;
  if (!ev.tags.some(t => t[0] === 'd' && t[1])) return false;
  return ev.tags.some(t => t[0] === 't' && t[1] === VIDEO_TAG);
}

/**
 * Parse a video event into a BCHVideo.
 *
 * Returns null when the event isn't an active BCHNostr video or has no
 * playable URL. Every field is re-sanitized here: the signature proves the
 * author, not that the payload is safe to render.
 */
export function parseVideo(ev: Event): BCHVideo | null {
  if (!isActiveVideo(ev)) return null;

  const dTag = ev.tags.find(t => t[0] === 'd')?.[1] || '';
  if (!dTag) return null;

  // The media URL, in order of preference: imeta (NIP-71 canonical),
  // the `origin` tag's original URL, then a plain `r` reference.
  const imeta = ev.tags.find(t => t[0] === 'imeta');
  const originTag = ev.tags.find(t => t[0] === 'origin');
  const rawUrl =
    (imeta ? imetaValue(imeta, 'url') : '') ||
    (originTag?.[3] || '') ||
    (ev.tags.find(t => t[0] === 'r')?.[1] || '');

  const playback = resolveVideoUrl(rawUrl);
  // No usable URL means nothing to watch. A page with a broken embed is
  // still useful (discussion survives), but an event with no URL at all
  // is not a video.
  if (!playback) return null;

  const catTag = ev.tags.find(t => t[0] === 't' && t[1]?.startsWith(VIDEO_CAT_PREFIX));
  const category = normalizeCategory(
    catTag ? catTag[1].slice(VIDEO_CAT_PREFIX.length) : '',
  );

  const hashtags = [...new Set(
    ev.tags
      .filter(t => t[0] === 't' && typeof t[1] === 'string')
      .map(t => t[1])
      .filter(t => t !== VIDEO_TAG && !t.startsWith(VIDEO_CAT_PREFIX))
      .map(normalizeHashtag)
      .filter(Boolean),
  )].slice(0, MAX_HASHTAGS);

  const durationRaw =
    parseInt(ev.tags.find(t => t[0] === 'duration')?.[1] || '', 10) ||
    parseFloat(imeta ? imetaValue(imeta, 'duration') : '') ||
    0;
  const duration = Number.isFinite(durationRaw) && durationRaw > 0
    ? Math.min(Math.floor(durationRaw), MAX_DURATION)
    : 0;

  const publishedRaw = parseInt(ev.tags.find(t => t[0] === 'published_at')?.[1] || '', 10);
  const publishedAt = Number.isFinite(publishedRaw) && publishedRaw > 0
    // A published_at in the future is either a clock error or a bid for
    // permanent top-of-feed placement. Clamp it to the event time.
    ? Math.min(publishedRaw, ev.created_at)
    : ev.created_at;

  // Thumbnail: creator-supplied first, then the provider's predictable
  // poster. Re-validated because it lands in an `img src`.
  const thumbnailUrl =
    safeUrl(imeta ? imetaValue(imeta, 'image') : '') ||
    playback.thumbnailUrl;

  return {
    id: dTag,
    pubkey: ev.pubkey,
    title: clip(stripHtml(ev.tags.find(t => t[0] === 'title')?.[1], MAX_TITLE), MAX_TITLE)
      || 'Untitled video',
    description: stripHtml(ev.content, MAX_DESCRIPTION),
    url: playback.sourceUrl,
    thumbnailUrl,
    duration,
    category,
    hashtags,
    publishedAt,
    created_at: ev.created_at,
    provider: playback.provider,
    // No provider we support publishes a trustworthy view count over
    // oEmbed, so this stays 0 and the UI shows no external view number.
    externalViews: 0,
    playback,
    event: ev,
  };
}

/** Human label for the provider hosting a video. */
export function videoProviderLabel(video: BCHVideo): string {
  return providerLabel(video.provider);
}

// ── Playlists ─────────────────────────────────────────────────────

export interface PlaylistDraft {
  title: string;
  description: string;
  image: string;
  /** Video coordinates, in playback order. */
  videoRefs: string[];
}

const MAX_PLAYLIST_ITEMS = 200;

/** Build tags + content for a NIP-51 video curation set. */
export function buildPlaylistEvent(
  dTag: string, draft: PlaylistDraft,
): { tags: string[][]; content: string } {
  const refs = [...new Set(draft.videoRefs.filter(r => !!parseVideoCoordinate(r)))]
    .slice(0, MAX_PLAYLIST_ITEMS);

  const tags: string[][] = [
    ['d', dTag],
    ['t', PLAYLIST_TAG],
    ['title', clip(stripHtml(draft.title, 200), 200) || 'Untitled playlist'],
    ['alt', 'A BCHNostr video playlist'],
  ];
  const image = safeUrlLoose(draft.image);
  if (image) tags.push(['image', image]);
  const description = clip(stripHtml(draft.description, 1000), 1000);
  if (description) tags.push(['description', description]);
  // `a` tags carry the addressable video coordinates, in order.
  for (const ref of refs) tags.push(['a', ref]);

  return { tags, content: '' };
}

/** Tags + content that retire a playlist. */
export function retirePlaylistEvent(dTag: string): { tags: string[][]; content: string } {
  return { tags: [['d', dTag], ['alt', 'Playlist removed']], content: '' };
}

/** Parse a playlist event. Null when it isn't an active BCHNostr playlist. */
export function parsePlaylist(ev: Event): VideoPlaylist | null {
  if (ev.kind !== PLAYLIST_KIND) return null;
  if (!ev.tags.some(t => t[0] === 't' && t[1] === PLAYLIST_TAG)) return null;
  const dTag = ev.tags.find(t => t[0] === 'd')?.[1] || '';
  if (!dTag) return null;

  const videoRefs = ev.tags
    .filter(t => t[0] === 'a' && typeof t[1] === 'string')
    .map(t => t[1])
    .filter(ref => !!parseVideoCoordinate(ref))
    .slice(0, MAX_PLAYLIST_ITEMS);

  // A retired playlist keeps its `d` tag but drops the discovery tag, so
  // reaching here with zero items just means "an empty playlist".
  return {
    id: dTag,
    pubkey: ev.pubkey,
    title: clip(stripHtml(ev.tags.find(t => t[0] === 'title')?.[1], 200), 200)
      || 'Untitled playlist',
    description: stripHtml(ev.tags.find(t => t[0] === 'description')?.[1], 1000),
    image: safeUrl(ev.tags.find(t => t[0] === 'image')?.[1]),
    videoRefs,
    created_at: ev.created_at,
    event: ev,
  };
}

// ── Promotion ─────────────────────────────────────────────────────

export interface VideoBoostData {
  /** The promoted video's creator. */
  videoPubkey: string;
  /** The promoted video's `d` tag. Empty string promotes the channel. */
  videoDTag: string;
  priceSats: number;
  expiresAt: number;
  boostedBy: string;
  bidUsd?: number;
  txid?: string;
  /**
   * True when the payer bought this as a PROFESSIONAL promotion (they hold
   * a kind 30499 professional profile and chose to promote as a business).
   * It changes only the label — 📢 Sponsored instead of 🚀 Boosted — never
   * the ranking. Viewers are entitled to know which they're looking at.
   */
  sponsored?: boolean;
}

/** d-tag for a video boost. Keyed so re-boosting replaces, never stacks. */
export function videoBoostDTag(pubkey: string, dTag: string): string {
  return `${VIDEO_BOOST_D_TAG_PREFIX}${pubkey}/${dTag || 'channel'}`;
}

/** Parse a video-boost event, sanitizing its expiry. Null if malformed. */
export function parseVideoBoost(ev: Event): VideoBoostData | null {
  try {
    const parsed: unknown = JSON.parse(ev.content);
    if (!parsed || typeof parsed !== 'object') return null;
    const data = parsed as Record<string, unknown>;

    const videoPubkey = typeof data.videoPubkey === 'string' ? data.videoPubkey : '';
    if (!/^[0-9a-f]{64}$/i.test(videoPubkey)) return null;

    let expiresAt = Number(data.expiresAt);
    if (!Number.isFinite(expiresAt) || expiresAt <= 0) return null;
    // Milliseconds sneak in; unix seconds don't reach 10e9 until 2286.
    if (expiresAt > 10_000_000_000) expiresAt = Math.floor(expiresAt / 1000);
    // Clamp the window so a fabricated expiry can't buy a permanent slot.
    const maxExpiry = ev.created_at + VIDEO_BOOST_DURATION;
    if (expiresAt > maxExpiry) expiresAt = maxExpiry;

    const priceSats = Number(data.priceSats);

    return {
      videoPubkey: videoPubkey.toLowerCase(),
      videoDTag: typeof data.videoDTag === 'string' ? data.videoDTag : '',
      priceSats: Number.isFinite(priceSats) && priceSats > 0 ? Math.floor(priceSats) : 0,
      expiresAt,
      // The PAYER is the event's author, not whatever the payload claims.
      boostedBy: ev.pubkey,
      bidUsd: typeof data.bidUsd === 'number' ? data.bidUsd : undefined,
      txid: typeof data.txid === 'string'
        ? data.txid
        : ev.tags.find(t => t[0] === 'txid')?.[1],
      sponsored: data.sponsored === true,
    };
  } catch {
    return null;
  }
}

/** Active video promotions, highest bid first, one per target. */
export function activeVideoBoosts(events: Event[]): VideoBoostData[] {
  const now = Math.floor(Date.now() / 1000);
  const byTarget = new Map<string, VideoBoostData>();
  for (const ev of events) {
    const data = parseVideoBoost(ev);
    if (!data || data.expiresAt <= now || data.priceSats <= 0) continue;
    const key = `${data.videoPubkey}:${data.videoDTag}`;
    const existing = byTarget.get(key);
    if (!existing || data.priceSats > existing.priceSats) byTarget.set(key, data);
  }
  return [...byTarget.values()].sort((a, b) => b.priceSats - a.priceSats);
}

// ── Local analytics ───────────────────────────────────────────────
//
// BCHNostr can honestly measure exactly two things: how often one of its
// own pages was opened, and how often someone pressed Play inside it. It
// cannot see plays on YouTube, and it cannot see watch time — the media is
// served by somebody else's infrastructure.
//
// So we count "video page views" and "play clicks", label them as exactly
// that, and never round them up into "views" or "watch time". Counters are
// per-browser localStorage tallies: no server, no cross-user aggregation,
// no tracking, and stated as such wherever they're shown.

const STATS_KEY = 'bchnostr:video-stats';
const STATS_MAX_KEYS = 300;

export interface VideoStats {
  /** Views of a video page, keyed by `<pubkey>:<dTag>`. */
  videoViews: Record<string, number>;
  /** Clicks on Play. NOT plays, and definitely not watch time. */
  playClicks: Record<string, number>;
  /** Views of a channel page, keyed by pubkey. */
  channelViews: Record<string, number>;
}

const EMPTY_STATS: VideoStats = { videoViews: {}, playClicks: {}, channelViews: {} };

export function getVideoStats(): VideoStats {
  try {
    const raw = localStorage.getItem(STATS_KEY);
    if (!raw) return { ...EMPTY_STATS };
    const parsed = JSON.parse(raw) as Partial<VideoStats>;
    return {
      videoViews: parsed.videoViews || {},
      playClicks: parsed.playClicks || {},
      channelViews: parsed.channelViews || {},
    };
  } catch {
    return { ...EMPTY_STATS };
  }
}

/** Trim a counter map so it can't grow without bound. */
function trimCounters(map: Record<string, number>): Record<string, number> {
  const keys = Object.keys(map);
  if (keys.length <= STATS_MAX_KEYS) return map;
  const kept = keys.sort((a, b) => (map[b] || 0) - (map[a] || 0)).slice(0, STATS_MAX_KEYS);
  const next: Record<string, number> = {};
  for (const k of kept) next[k] = map[k];
  return next;
}

function bump(bucket: keyof VideoStats, key: string) {
  if (!key) return;
  try {
    const stats = getVideoStats();
    const map = stats[bucket];
    map[key] = (map[key] || 0) + 1;
    stats[bucket] = trimCounters(map);
    localStorage.setItem(STATS_KEY, JSON.stringify(stats));
  } catch {
    // Storage full or disabled — analytics are a nicety, never a
    // precondition for watching a video.
  }
}

function statsKey(pubkey: string, dTag: string): string {
  return `${pubkey}:${dTag}`;
}

export function recordVideoView(pubkey: string, dTag: string) {
  bump('videoViews', statsKey(pubkey, dTag));
}

/** Record a click on Play. This is a play CLICK, never a confirmed view. */
export function recordPlayClick(pubkey: string, dTag: string) {
  bump('playClicks', statsKey(pubkey, dTag));
}

export function recordChannelView(pubkey: string) {
  bump('channelViews', pubkey);
}

/** This browser's recorded page views for one video. */
export function videoViewCount(pubkey: string, dTag: string): number {
  return getVideoStats().videoViews[statsKey(pubkey, dTag)] || 0;
}

/** This browser's recorded play clicks for one video. */
export function playClickCount(pubkey: string, dTag: string): number {
  return getVideoStats().playClicks[statsKey(pubkey, dTag)] || 0;
}

/** Aggregate the local counters for one channel. */
export function summarizeChannelStats(pubkey: string): {
  channelViews: number;
  videoViews: number;
  playClicks: number;
} {
  const stats = getVideoStats();
  const prefix = `${pubkey}:`;
  const sumPrefixed = (map: Record<string, number>) =>
    Object.entries(map).reduce((total, [k, v]) => (k.startsWith(prefix) ? total + v : total), 0);

  return {
    channelViews: stats.channelViews[pubkey] || 0,
    videoViews: sumPrefixed(stats.videoViews),
    playClicks: sumPrefixed(stats.playClicks),
  };
}

// ── Watch history ─────────────────────────────────────────────────
//
// Deliberately minimal: a coordinate, a timestamp, and a title so the list
// renders without a relay round-trip. No watch positions, no durations, no
// device info, no user id. It never leaves the browser, and the user can
// erase it in one click.

const HISTORY_KEY = 'bchnostr:video-history';
const HISTORY_LIMIT = 100;

export interface HistoryEntry {
  pubkey: string;
  dTag: string;
  /** Cached for instant rendering. Re-sanitized on read anyway. */
  title: string;
  thumbnailUrl: string;
  /** Unix ms of the most recent open. */
  watchedAt: number;
}

export function getWatchHistory(): HistoryEntry[] {
  try {
    const raw = localStorage.getItem(HISTORY_KEY);
    if (!raw) return [];
    const parsed: unknown = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter((e): e is HistoryEntry =>
        !!e && typeof e === 'object' &&
        typeof (e as HistoryEntry).pubkey === 'string' &&
        /^[0-9a-f]{64}$/i.test((e as HistoryEntry).pubkey) &&
        typeof (e as HistoryEntry).dTag === 'string' &&
        !!(e as HistoryEntry).dTag)
      .map(e => ({
        pubkey: e.pubkey.toLowerCase(),
        dTag: e.dTag,
        // Cached strings are re-sanitized: localStorage is writable by any
        // script that ever ran on this origin.
        title: clip(stripHtml(e.title, MAX_TITLE), MAX_TITLE),
        thumbnailUrl: safeUrl(e.thumbnailUrl),
        watchedAt: Number.isFinite(e.watchedAt) ? e.watchedAt : 0,
      }))
      .sort((a, b) => b.watchedAt - a.watchedAt)
      .slice(0, HISTORY_LIMIT);
  } catch {
    return [];
  }
}

/** Record (or bump) a video in the local watch history. */
export function recordWatch(video: BCHVideo) {
  try {
    const history = getWatchHistory()
      .filter(e => !(e.pubkey === video.pubkey && e.dTag === video.id));
    history.unshift({
      pubkey: video.pubkey,
      dTag: video.id,
      title: video.title,
      thumbnailUrl: video.thumbnailUrl,
      watchedAt: Date.now(),
    });
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, HISTORY_LIMIT)));
  } catch {}
}

/** Erase the local watch history. */
export function clearWatchHistory() {
  try { localStorage.removeItem(HISTORY_KEY); } catch {}
}

/** Remove one entry from the local watch history. */
export function removeFromHistory(pubkey: string, dTag: string) {
  try {
    const next = getWatchHistory().filter(e => !(e.pubkey === pubkey && e.dTag === dTag));
    localStorage.setItem(HISTORY_KEY, JSON.stringify(next));
  } catch {}
}

/** Coordinates the user has already opened — used to avoid re-recommending. */
export function watchedCoordinates(): Set<string> {
  return new Set(getWatchHistory().map(e => videoCoordinate(e.pubkey, e.dTag)));
}

// ── Interests ─────────────────────────────────────────────────────
//
// A per-browser tally of which categories the user actually opens. It is
// the only "profile" the recommender keeps, it never leaves the device,
// and it is derived purely from the user's own clicks.

const INTEREST_KEY = 'bchnostr:video-interests';

export function getCategoryInterests(): Record<string, number> {
  try {
    const raw = localStorage.getItem(INTEREST_KEY);
    if (!raw) return {};
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== 'object') return {};
    const out: Record<string, number> = {};
    for (const [k, v] of Object.entries(parsed as Record<string, unknown>)) {
      if (VIDEO_CATEGORY_IDS.has(k) && typeof v === 'number' && Number.isFinite(v) && v > 0) {
        out[k] = Math.min(v, 1000);
      }
    }
    return out;
  } catch {
    return {};
  }
}

export function recordCategoryInterest(category: string) {
  const cat = normalizeCategory(category);
  try {
    const interests = getCategoryInterests();
    interests[cat] = (interests[cat] || 0) + 1;
    localStorage.setItem(INTEREST_KEY, JSON.stringify(interests));
  } catch {}
}

export function clearCategoryInterests() {
  try { localStorage.removeItem(INTEREST_KEY); } catch {}
}

// ── Engagement signals ────────────────────────────────────────────

/** Per-video engagement, aggregated from Nostr events. */
export interface VideoEngagement {
  /** Distinct people who tipped this video. */
  uniqueTippers: number;
  /** Total sats tipped (relay-dependent, so a signal not a ledger). */
  tipSats: number;
  /** Comments (kind 1111) on this video. */
  comments: number;
  /** Distinct people who reacted. */
  reactions: number;
  /** Distinct people who did ANYTHING — the anti-brigading denominator. */
  uniqueParticipants: number;
  /** Engagement events in the last 24h — the "hot right now" signal. */
  recentActivity: number;
}

export const EMPTY_ENGAGEMENT: VideoEngagement = {
  uniqueTippers: 0, tipSats: 0, comments: 0, reactions: 0,
  uniqueParticipants: 0, recentActivity: 0,
};

// ── Recommendation & trending ─────────────────────────────────────
//
// A transparent, inspectable scoring function — no machine learning, no
// opaque model, and no signal the user can't see the basis of. Every
// weight below is a deliberate editorial choice, and they are stated in
// the UI ("Why am I seeing this?") rather than hidden.

const REC_WEIGHTS = {
  /** A creator the user follows is the strongest honest signal we have. */
  followedCreator: 100,
  /** Categories the user actually opens, scaled by how often. */
  interestCategory: 8,
  /** Engagement, deliberately modest so it can't outrank a real follow. */
  uniqueTipper: 6,
  comment: 3,
  reaction: 1,
  /** Log-scaled so one whale tip can't manufacture a trending video. */
  tipSatsLog: 4,
  /** Small nudge for creators the community follows broadly. */
  creatorFollowerLog: 3,
} as const;

/** How quickly recency stops mattering. 7 days ≈ half weight. */
const RECENCY_HALF_LIFE_HOURS = 168;

export interface RecommendationSignals {
  /** Pubkeys the viewer follows (kind 3). */
  follows: Set<string>;
  /** Category → how many times the viewer opened a video in it. */
  interests: Record<string, number>;
  /** Coordinates already opened — heavily demoted to avoid repetition. */
  watched: Set<string>;
  /** Creator pubkey → follower count, as a popularity signal. */
  creatorFollowers: Map<string, number>;
  /** Video coordinate → engagement. */
  engagement: Map<string, VideoEngagement>;
}

export interface ScoredVideo {
  video: BCHVideo;
  score: number;
  /** Human-readable reasons, shown to the viewer verbatim. */
  reasons: string[];
}

/** Age in hours, floored at 0. */
function ageHours(timestamp: number): number {
  return Math.max(0, (Date.now() / 1000 - timestamp) / 3600);
}

/** Exponential recency multiplier in (0, 1]. */
function recencyFactor(timestamp: number): number {
  return Math.pow(0.5, ageHours(timestamp) / RECENCY_HALF_LIFE_HOURS);
}

/**
 * Score a video for the "Recommended" rail.
 *
 * Priority order, matching the stated product intent:
 *   1. followed creators
 *   2. relevant categories
 *   3. recent videos
 *   4. engagement
 *   5. broad community activity
 *
 * Already-watched videos are demoted rather than removed, so a small
 * catalogue doesn't produce an empty rail.
 */
export function scoreVideo(video: BCHVideo, signals: RecommendationSignals): ScoredVideo {
  const coord = videoCoordinate(video.pubkey, video.id);
  const eng = signals.engagement.get(coord) || EMPTY_ENGAGEMENT;
  const reasons: string[] = [];
  let score = 0;

  if (signals.follows.has(video.pubkey)) {
    score += REC_WEIGHTS.followedCreator;
    reasons.push('From a creator you follow');
  }

  const interest = signals.interests[video.category] || 0;
  if (interest > 0) {
    // Square-rooted: the tenth video in a category shouldn't count ten
    // times as much as the first, or the feed collapses to one topic.
    score += Math.sqrt(interest) * REC_WEIGHTS.interestCategory;
    reasons.push(`You watch ${videoCategoryLabel(video.category)}`);
  }

  const engagementScore =
    eng.uniqueTippers * REC_WEIGHTS.uniqueTipper +
    eng.comments * REC_WEIGHTS.comment +
    eng.reactions * REC_WEIGHTS.reaction +
    Math.log10(1 + eng.tipSats) * REC_WEIGHTS.tipSatsLog;
  score += engagementScore;
  if (eng.uniqueTippers >= 2) reasons.push(`${eng.uniqueTippers} people tipped this`);
  else if (eng.comments >= 3) reasons.push(`${eng.comments} comments`);

  const followers = signals.creatorFollowers.get(video.pubkey) || 0;
  if (followers > 0) score += Math.log10(1 + followers) * REC_WEIGHTS.creatorFollowerLog;

  // Recency multiplies rather than adds, so a great old video still
  // surfaces but a fresh one from a followed creator wins.
  score *= 0.35 + 0.65 * recencyFactor(video.publishedAt);
  if (ageHours(video.publishedAt) < 72) reasons.push('New');

  // Seen it? Push it down hard, but don't erase it.
  if (signals.watched.has(coord)) {
    score *= 0.15;
    reasons.push('Watched');
  }

  return { video, score, reasons };
}

const TRENDING_WEIGHTS = {
  uniqueTipper: 8,
  comment: 4,
  reaction: 1,
  tipSatsLog: 6,
  /** Recent activity is what makes trending *trending*. */
  recentActivity: 5,
  /** Local play clicks — an honest but weak, per-browser signal. */
  playClick: 0.5,
} as const;

/** Trending is a 14-day window: older than that isn't trending. */
const TRENDING_MAX_AGE_HOURS = 14 * 24;

/**
 * Rank videos for Trending.
 *
 * Explicitly NOT ranked by raw view count — we don't have a trustworthy
 * one, and a raw count rewards whoever can generate the most requests. We
 * rank by *who* engaged (unique tippers, unique reactors) with time decay,
 * then cap how many entries any single creator may occupy so one prolific
 * account cannot take over the page.
 */
export function rankTrending(
  videos: BCHVideo[],
  engagement: Map<string, VideoEngagement>,
  opts: { maxPerCreator?: number; limit?: number } = {},
): BCHVideo[] {
  const maxPerCreator = opts.maxPerCreator ?? 2;
  const limit = opts.limit ?? 30;

  const scored = videos
    .filter(v => ageHours(v.publishedAt) <= TRENDING_MAX_AGE_HOURS)
    .map(video => {
      const coord = videoCoordinate(video.pubkey, video.id);
      const eng = engagement.get(coord) || EMPTY_ENGAGEMENT;
      const raw =
        eng.uniqueTippers * TRENDING_WEIGHTS.uniqueTipper +
        eng.comments * TRENDING_WEIGHTS.comment +
        eng.reactions * TRENDING_WEIGHTS.reaction +
        Math.log10(1 + eng.tipSats) * TRENDING_WEIGHTS.tipSatsLog +
        eng.recentActivity * TRENDING_WEIGHTS.recentActivity +
        Math.min(playClickCount(video.pubkey, video.id), 20) * TRENDING_WEIGHTS.playClick;
      // Hacker-News style decay, so a week-old hit yields to today's.
      const score = raw / Math.pow(ageHours(video.publishedAt) / 24 + 2, 1.5);
      return { video, score };
    })
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score);

  // Diversity pass — no creator may dominate the feed.
  const perCreator = new Map<string, number>();
  const out: BCHVideo[] = [];
  for (const { video } of scored) {
    const used = perCreator.get(video.pubkey) || 0;
    if (used >= maxPerCreator) continue;
    perCreator.set(video.pubkey, used + 1);
    out.push(video);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Order videos for the "Recommended" rail, with the same anti-domination
 * cap Trending uses.
 */
export function rankRecommended(
  videos: BCHVideo[],
  signals: RecommendationSignals,
  opts: { maxPerCreator?: number; limit?: number } = {},
): ScoredVideo[] {
  const maxPerCreator = opts.maxPerCreator ?? 3;
  const limit = opts.limit ?? 40;

  const scored = videos
    .map(v => scoreVideo(v, signals))
    .sort((a, b) => b.score - a.score);

  const perCreator = new Map<string, number>();
  const out: ScoredVideo[] = [];
  for (const entry of scored) {
    const used = perCreator.get(entry.video.pubkey) || 0;
    if (used >= maxPerCreator) continue;
    perCreator.set(entry.video.pubkey, used + 1);
    out.push(entry);
    if (out.length >= limit) break;
  }
  return out;
}

/**
 * Videos related to the one being watched.
 *
 * Same creator first, then same category, then shared hashtags — a
 * transparent rule, not a black box.
 */
export function relatedVideos(
  video: BCHVideo, all: BCHVideo[], limit = 12,
): BCHVideo[] {
  const tags = new Set(video.hashtags);
  return all
    .filter(v => !(v.pubkey === video.pubkey && v.id === video.id))
    .map(v => {
      let score = 0;
      if (v.pubkey === video.pubkey) score += 10;
      if (v.category === video.category) score += 6;
      score += v.hashtags.filter(t => tags.has(t)).length * 3;
      score += recencyFactor(v.publishedAt) * 2;
      return { v, score };
    })
    .filter(s => s.score > 0)
    .sort((a, b) => b.score - a.score)
    .slice(0, limit)
    .map(s => s.v);
}

// ── Display helpers ───────────────────────────────────────────────

/** Format seconds the way a video player does: "12:34" / "1:02:33". */
export function formatVideoDuration(seconds: number): string {
  if (!seconds || seconds <= 0) return '';
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  const s = Math.floor(seconds % 60);
  if (h > 0) return `${h}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Compact count: "1.2K", "3.4M". */
export function formatCount(n: number): string {
  if (!Number.isFinite(n) || n <= 0) return '0';
  if (n >= 1e6) return `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(n >= 1e4 ? 0 : 1)}K`;
  return String(Math.floor(n));
}

/** Relative publication date, YouTube style. */
export function formatPublished(timestamp: number): string {
  if (!timestamp) return '';
  const seconds = Math.floor(Date.now() / 1000 - timestamp);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minute${minutes === 1 ? '' : 's'} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? '' : 's'} ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? '' : 's'} ago`;
  const weeks = Math.floor(days / 7);
  if (weeks < 5) return `${weeks} week${weeks === 1 ? '' : 's'} ago`;
  const months = Math.floor(days / 30);
  if (months < 12) return `${months} month${months === 1 ? '' : 's'} ago`;
  const years = Math.floor(days / 365);
  return `${years} year${years === 1 ? '' : 's'} ago`;
}

/** The text posted to Nostr when someone shares a video. */
export function buildVideoShareText(video: BCHVideo, appUrl: string): string {
  return [
    `🎥 ${video.title}`,
    // The provider's own page, so the share drives real views to the creator.
    video.url,
    `Watch & discuss on BCHNostr: ${appUrl}`,
  ].join('\n\n');
}

/** Re-export for convenience so components import from one module. */
export { parseVideoUrl, resolveVideoUrl };
export type { ProviderId, ResolvedVideo };
